import { saveUserSetting } from "@/shared/stores/saveUserSetting";
import { userSettingsStore } from "@/shared/stores/UserSettingsStore";

const ACCOUNT_TYPE_CHANGE_EVENT = "accountTypeChange";

const SECTION = "account-type";
const KEY = "global";

const settingIdRef: { current: string | null } = { current: null };

type AccountTypeValue = "demo" | "real";

function applyLocal(type: AccountTypeValue): void {
  localStorage.setItem("accountType", type);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(ACCOUNT_TYPE_CHANGE_EVENT, { detail: type }));
  }
}

export const accountType = {
  set(type: AccountTypeValue): Promise<void> {
    applyLocal(type);
    // Persist server-side so the choice follows the user across devices.
    return saveUserSetting(SECTION, KEY, { type }, settingIdRef);
  },
  /** Apply a short-lived UI mode without changing the user's persisted preference. */
  setTemporary(type: AccountTypeValue): void {
    applyLocal(type);
  },
  get(): AccountTypeValue | null {
    return localStorage.getItem("accountType") as AccountTypeValue | null;
  },
  remove(): void {
    localStorage.removeItem("accountType");
    settingIdRef.current = null;
  },
  // Subscribe to account type changes
  subscribe(callback: (type: AccountTypeValue) => void): () => void {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<AccountTypeValue>;
      callback(customEvent.detail);
    };

    if (typeof window !== "undefined") {
      window.addEventListener(ACCOUNT_TYPE_CHANGE_EVENT, handler);
      return () => window.removeEventListener(ACCOUNT_TYPE_CHANGE_EVENT, handler);
    }

    return () => {};
  },
  // Load the server-stored account type and apply it locally when it differs.
  async hydrate(): Promise<void> {
    try {
      await userSettingsStore.load();
      const entry = userSettingsStore.getSetting(SECTION, KEY);
      if (!entry) return;
      settingIdRef.current = entry.id;
      const value = entry.value as { type?: unknown } | undefined;
      const serverType = value?.type;
      if ((serverType === "demo" || serverType === "real") && serverType !== this.get()) {
        applyLocal(serverType);
      }
    } catch {
      // silent — fall back to the locally stored value
    }
  },
};
